/**
 * Declarative milestone registry for BUDOVA (and ROZVADEC welding).
 * Engines remain the runtime source for completion rules; this registry drives
 * UI field lists, auto-open-spec, and phase-confirm wiring without giant when-trees.
 */

import {
  BUDOVA_MILESTONE_IDS,
  BUDOVA_MILESTONE_LABELS,
  BUDOVA_REQUIRED_MILESTONES,
  type BudovaMilestoneId,
} from './budova-milestone-engine';
import {
  ROZVADEC_MILESTONE_IDS,
  ROZVADEC_MILESTONE_LABELS,
  type RozvadecMilestoneId,
} from './rozvadec-milestone-engine';
import type { SpecFieldId } from './spec-field-registry';
import { PHASE_CONFIRM_FIELDS_BY_MILESTONE } from './spec-field-registry';
import type { BudovaBuildingType } from './budova-building-type.constants';

export type MilestoneObjectType = 'BUDOVA' | 'ROZVADEC';

export type MilestoneDef = {
  id: string;
  objectType: MilestoneObjectType;
  labelCs: string;
  /** Spec fields required before the milestone can complete. */
  requiredSpecFields: readonly SpecFieldId[];
  /** Spec fields shown in phase-confirm review when completing this milestone. */
  phaseConfirmFields: readonly SpecFieldId[];
  /**
 * Auto-open dedicated spec sheet when user picks this milestone (popup 2 → 3).
 * SSOT for web / Android / iOS — do not hardcode AUTO_OPEN sets on clients.
 */
  autoOpenSpec: boolean;
  /**
   * Auto-open spec after an explicit enter into popup 3 (pick / intentional handoff).
   * Fresh object click never skips the picker — see `milestone-popup-mode.ts`.
   */
  autoOpenSpecOnEnter: boolean;
  /** Map stripe / half-circle hooks (engine/map modules consume these ids). */
  mapStripeHook: string | null;
};

/** Pick-path auto-open (popup 2 → 3). */
const BUDOVA_AUTO_OPEN_SPEC: ReadonlySet<string> = new Set([
  'ZAVRTANY',
  'ZRIZENE_VR',
  'POSTAVENA_BABOVKA',
  'ZAFOUKNUTO',
]);

/** Enter-path auto-open when skipping picker into popup 3 (excludes ZAVRTANY). */
const BUDOVA_AUTO_OPEN_SPEC_ON_ENTER: ReadonlySet<string> = new Set([
  'ZRIZENE_VR',
  'POSTAVENA_BABOVKA',
  'ZAFOUKNUTO',
]);

const BUDOVA_MAP_STRIPE: Record<string, string | null> = {
  ZAVRTANY: 'budova-milestone-map:ZAVRTANY',
  ZRIZENE_VR: 'budova-milestone-map:ZRIZENE_VR',
  ODBOCENO: 'budova-milestone-map:ODBOCENO',
  POSTAVENA_BABOVKA: 'budova-milestone-map:POSTAVENA_BABOVKA',
  ZAFOUKNUTO: 'budova-milestone-map:ZAFOUKNUTO',
};

function budovaRequiredSpecFields(id: BudovaMilestoneId): readonly SpecFieldId[] {
  switch (id) {
    case 'ZAVRTANY':
      return ['UKONCENI_V_RD'];
    case 'ZRIZENE_VR':
      return ['HP_TOTAL', 'HP_NEBYT', 'FLOORS', 'HORIZONTALNI_PROPOJE'];
    case 'POSTAVENA_BABOVKA':
      return ['BABOVKA_PLACEMENT'];
    case 'ZAFOUKNUTO':
      return ['ZAFUK'];
    case 'ODBOCENO':
      return ['NAPOJENE_MT'];
    default:
      return [];
  }
}

export const BUDOVA_MILESTONE_REGISTRY: readonly MilestoneDef[] = BUDOVA_MILESTONE_IDS.map((id) => ({
  id,
  objectType: 'BUDOVA' as const,
  labelCs: BUDOVA_MILESTONE_LABELS[id],
  requiredSpecFields: budovaRequiredSpecFields(id),
  phaseConfirmFields: PHASE_CONFIRM_FIELDS_BY_MILESTONE[id] ?? [],
  autoOpenSpec: BUDOVA_AUTO_OPEN_SPEC.has(id),
  autoOpenSpecOnEnter: BUDOVA_AUTO_OPEN_SPEC_ON_ENTER.has(id),
  mapStripeHook: BUDOVA_MAP_STRIPE[id] ?? null,
}));

export const ROZVADEC_MILESTONE_REGISTRY: readonly MilestoneDef[] = ROZVADEC_MILESTONE_IDS.map(
  (id: RozvadecMilestoneId) => ({
    id,
    objectType: 'ROZVADEC' as const,
    labelCs: ROZVADEC_MILESTONE_LABELS[id],
    requiredSpecFields: ['WELD_COUNT'] as const,
    phaseConfirmFields: PHASE_CONFIRM_FIELDS_BY_MILESTONE[id] ?? (['WELD_COUNT'] as const),
    autoOpenSpec: false,
    autoOpenSpecOnEnter: false,
    mapStripeHook: `rozvadec-milestone-map:${id}`,
  }),
);

export const MILESTONE_REGISTRY: readonly MilestoneDef[] = [
  ...BUDOVA_MILESTONE_REGISTRY,
  ...ROZVADEC_MILESTONE_REGISTRY,
];

const BY_KEY = new Map<string, MilestoneDef>(
  MILESTONE_REGISTRY.map((m) => [`${m.objectType}:${m.id}`, m]),
);

export function getMilestoneDef(
  objectType: MilestoneObjectType | string,
  milestoneId: string,
): MilestoneDef | undefined {
  return BY_KEY.get(`${String(objectType).trim().toUpperCase()}:${milestoneId.trim().toUpperCase()}`);
}

export function milestonesForObjectType(objectType: MilestoneObjectType | string): readonly MilestoneDef[] {
  const t = String(objectType).trim().toUpperCase();
  return MILESTONE_REGISTRY.filter((m) => m.objectType === t);
}

export function requiredMilestoneIdsForBuildingType(
  buildingType: BudovaBuildingType | string | null | undefined,
): readonly BudovaMilestoneId[] {
  const key = String(buildingType ?? '')
    .trim()
    .toUpperCase() as BudovaBuildingType;
  return BUDOVA_REQUIRED_MILESTONES[key] ?? [];
}

/** Milestones that show phase-confirm review on complete (BUDOVA). */
export const BUDOVA_MILESTONES_PHASE_CONFIRM_ON_COMPLETE: readonly string[] = BUDOVA_MILESTONE_REGISTRY
  .filter((m) => m.phaseConfirmFields.length > 0 && m.id !== 'ODBOCENO')
  .map((m) => m.id);

/** Milestones that show phase-confirm review on complete (ROZVADEC). */
export const ROZVADEC_MILESTONES_PHASE_CONFIRM_ON_COMPLETE: readonly string[] =
  ROZVADEC_MILESTONE_REGISTRY.map((m) => m.id);

/** BUDOVA pick-path auto-open ids (from registry `autoOpenSpec`). */
export const BUDOVA_MILESTONES_AUTO_OPEN_ON_PICK: readonly string[] = BUDOVA_MILESTONE_REGISTRY
  .filter((m) => m.autoOpenSpec)
  .map((m) => m.id);

/** BUDOVA enter-path auto-open ids (from registry `autoOpenSpecOnEnter`). */
export const BUDOVA_MILESTONES_AUTO_OPEN_ON_ENTER: readonly string[] = BUDOVA_MILESTONE_REGISTRY
  .filter((m) => m.autoOpenSpecOnEnter)
  .map((m) => m.id);

export function shouldAutoOpenBudovaMilestoneSpec(
  milestoneId: string | null | undefined,
  mode: 'enter' | 'pick' = 'pick',
): boolean {
  const def = getMilestoneDef('BUDOVA', milestoneId ?? '');
  if (!def) return false;
  return mode === 'enter' ? def.autoOpenSpecOnEnter : def.autoOpenSpec;
}

export type MilestoneRegistryExport = {
  milestones: readonly MilestoneDef[];
  budovaPhaseConfirmOnComplete: readonly string[];
  rozvadecPhaseConfirmOnComplete: readonly string[];
  requiredByBuildingType: Record<string, readonly string[]>;
};

export function buildMilestoneRegistryExport(): MilestoneRegistryExport {
  return {
    milestones: MILESTONE_REGISTRY,
    budovaPhaseConfirmOnComplete: BUDOVA_MILESTONES_PHASE_CONFIRM_ON_COMPLETE,
    rozvadecPhaseConfirmOnComplete: ROZVADEC_MILESTONES_PHASE_CONFIRM_ON_COMPLETE,
    requiredByBuildingType: { ...BUDOVA_REQUIRED_MILESTONES },
  };
}
